import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatDetail } from "@/lib/api";
import { HardHat, CheckCircle2, Loader2, Send } from "lucide-react";

const COUNTRIES = ["South Africa","Zimbabwe","Zambia","Botswana","Namibia","Mozambique","Other"];

export default function ApplyBuilderPage() {
  const [form, setForm] = useState({
    contact_name: "", company: "", country: "", city: "",
    phone: "", email: "", specialties: "",
    references: "", sample_pricing: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.country) {
      toast.error("Please share at least your name and country.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/builder-applications", form);
      setDone(true);
      toast.success("Application received. We will contact you within 5 working days.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <main className="max-w-4xl mx-auto px-4 md:px-8 py-14 md:py-20" data-testid="apply-builder-page">
      <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Apply · Verified Builders Directory</span></div>
      <h1 className="font-display text-4xl md:text-5xl mv-green-text mt-3">
        Build with dignity. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Join the list.</span>
      </h1>
      <p className="mt-4 max-w-2xl text-[15px]" style={{ color: "var(--mv-muted)" }}>
        Free listing. We vet every builder for fair wages, quality workmanship and transparent pricing.
        Only approved applicants appear on the public directory.
      </p>

      {done ? (
        <div className="mt-10 mv-card p-8 flex items-start gap-4" style={{ background: "linear-gradient(180deg, #fff, rgba(37,211,102,0.06))" }} data-testid="apply-builder-success">
          <CheckCircle2 size={26} style={{ color: "#0A7A2C" }}/>
          <div>
            <div className="font-display text-2xl mv-green-text">Application received.</div>
            <p className="mt-2 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
              We will review within 5 working days. You will hear from us on the phone or email you shared.
              We may ask for reference calls, on-site photos or a sample price sheet before publishing.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 mv-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="apply-builder-form">
          <Field label="Contact person *" value={form.contact_name} onChange={set("contact_name")} testid="ap-name"/>
          <Field label="Company name" value={form.company} onChange={set("company")} testid="ap-company"/>
          <SelectField label="Country *" value={form.country} onChange={set("country")} options={["", ...COUNTRIES]} testid="ap-country"/>
          <Field label="City / town" value={form.city} onChange={set("city")} testid="ap-city"/>
          <Field label="Phone / WhatsApp" value={form.phone} onChange={set("phone")} testid="ap-phone"/>
          <Field label="Email" value={form.email} onChange={set("email")} testid="ap-email"/>
          <Field label="Specialties" value={form.specialties} onChange={set("specialties")} placeholder="e.g. Family homes, roofing, solar" className="md:col-span-2" testid="ap-specialties"/>
          <TextArea label="3 recent client references (name + phone if possible)" rows={3} value={form.references} onChange={set("references")} className="md:col-span-2" testid="ap-refs"/>
          <TextArea label="Sample pricing (paste your price list, or describe)" rows={3} value={form.sample_pricing} onChange={set("sample_pricing")} className="md:col-span-2" testid="ap-pricing"/>
          <TextArea label="Anything else we should know" rows={2} value={form.notes} onChange={set("notes")} className="md:col-span-2" testid="ap-notes"/>

          <div className="md:col-span-2 pt-3 border-t mv-hairline flex items-center justify-between gap-4">
            <div className="text-[12.5px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
              Listing is free. We publish you only after independent vetting.
            </div>
            <button type="submit" disabled={loading} className="btn-green" data-testid="ap-submit">
              {loading ? <Loader2 className="animate-spin" size={16}/> : <Send size={16}/>}
              {loading ? "Sending…" : "Submit application"}
            </button>
          </div>
        </form>
      )}
    </main>
  );
}

const inputCls = "mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]";
function Field({ label, value, onChange, placeholder, className = "", testid }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{label}</span>
      <input value={value} onChange={onChange} placeholder={placeholder} className={inputCls} data-testid={testid}/>
    </label>
  );
}
function TextArea({ label, value, onChange, rows = 3, className = "", testid }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{label}</span>
      <textarea value={value} onChange={onChange} rows={rows} className={inputCls} data-testid={testid}/>
    </label>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{label}</span>
      <select value={value} onChange={onChange} className={inputCls} data-testid={testid}>
        {options.map((o) => <option key={o || "any"} value={o}>{o || "— Select —"}</option>)}
      </select>
    </label>
  );
}
